import { useState, useCallback } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import {
  useListFirefliesTranscripts,
  useSearchFirefliesTranscripts,
  useImportFirefliesTranscript,
} from "../../api/generated/generated";
import type { SearchScope } from "../../api/generated";
import { FirefliesSearchInput } from "./FirefliesSearchInput";
import { FirefliesTranscriptList } from "./FirefliesTranscriptList";
import { getErrorMessage } from "../../api/client";

interface FirefliesTranscriptBrowserProps {
  open: boolean;
  onClose: () => void;
  importedFirefliesIds: Set<string>;
}

export function FirefliesTranscriptBrowser({
  open,
  onClose,
  importedFirefliesIds,
}: FirefliesTranscriptBrowserProps) {
  const [skip, setSkip] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchScope, setSearchScope] = useState<SearchScope>("ALL");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [importError, setImportError] = useState<string | null>(null);

  const isSearchMode = searchKeyword.length > 0;

  const browseQuery = useListFirefliesTranscripts(
    { limit: 20, skip },
    { query: { enabled: open && !isSearchMode } },
  );
  const searchQuery = useSearchFirefliesTranscripts(
    { keyword: searchKeyword, scope: searchScope, limit: 20 },
    { query: { enabled: open && isSearchMode && searchKeyword.length > 0 } },
  );

  const activeQuery = isSearchMode ? searchQuery : browseQuery;
  const activeData =
    activeQuery.data?.status === 200 ? activeQuery.data.data : undefined;
  const transcripts = activeData?.transcripts ?? [];
  const hasMore = activeData?.has_more ?? false;

  const importMutation = useImportFirefliesTranscript();

  const handleSearch = useCallback((keyword: string, scope: SearchScope) => {
    setSearchKeyword(keyword);
    setSearchScope(scope);
    setSelectedIds(new Set());
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchKeyword("");
    setSelectedIds(new Set());
  }, []);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const selectable = transcripts
      .filter((t) => !importedFirefliesIds.has(t.id))
      .map((t) => t.id);
    setSelectedIds(new Set(selectable));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleLoadMore = () => {
    setSkip((prev) => prev + 20);
  };

  const handleImport = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setImporting(true);
    setImportProgress({ done: 0, total: ids.length });
    setImportError(null);

    let successCount = 0;
    for (const id of ids) {
      try {
        await importMutation.mutateAsync({ transcriptId: id });
        successCount++;
        setImportProgress({ done: successCount, total: ids.length });
      } catch (err) {
        setImportError(getErrorMessage(err));
        break;
      }
    }

    setImporting(false);
    setSelectedIds(new Set());

    if (successCount === ids.length) {
      onClose();
    }
  };

  const handleClose = () => {
    if (importing) return;
    setSkip(0);
    setSearchKeyword("");
    setSelectedIds(new Set());
    setImportError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import from Fireflies</DialogTitle>
      <DialogContent>
        <FirefliesSearchInput
          onSearch={handleSearch}
          onClear={handleClearSearch}
        />

        <FirefliesTranscriptList
          transcripts={transcripts}
          selectedIds={selectedIds}
          importedIds={importedFirefliesIds}
          onToggle={handleToggle}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          isLoading={activeQuery.isLoading}
        />

        {importing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Importing {importProgress.done} of {importProgress.total}...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(importProgress.done / importProgress.total) * 100}
            />
          </Box>
        )}

        {importError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {importError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={selectedIds.size === 0 || importing}
        >
          Import Selected ({selectedIds.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
